"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
} from "@mui/material"
import { Add, Edit, Visibility, Print, Payment } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function BillingManagement({ user }) {
  const { state, actions } = useApp()
  const [open, setOpen] = useState(false)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [selectedBill, setSelectedBill] = useState(null)
  const [formData, setFormData] = useState({
    patientName: "",
    patientId: "",
    services: [{ name: "", amount: 0 }],
    discount: 0,
    tax: 0,
  })
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    method: "",
    reference: "",
  })

  const handleOpen = (bill = null) => {
    if (bill) {
      setSelectedBill(bill)
      setFormData({
        patientName: bill.patientName,
        patientId: bill.patientId,
        services: bill.services,
        discount: 0,
        tax: 0,
      })
    } else {
      setSelectedBill(null)
      setFormData({
        patientName: "",
        patientId: "",
        services: [{ name: "", amount: 0 }],
        discount: 0,
        tax: 0,
      })
    }
    setOpen(true)
  }

  const handlePaymentOpen = (bill) => {
    setSelectedBill(bill)
    setPaymentData({
      amount: bill.totalAmount - bill.paidAmount,
      method: "",
      reference: "",
    })
    setPaymentOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setPaymentOpen(false)
    setSelectedBill(null)
  }

  const addService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { name: "", amount: 0 }],
    })
  }

  const updateService = (index, field, value) => {
    const updatedServices = formData.services.map((service, i) =>
      i === index ? { ...service, [field]: value } : service,
    )
    setFormData({ ...formData, services: updatedServices })
  }

  const removeService = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index),
    })
  }

  const calculateTotal = () => {
    const subtotal = formData.services.reduce((sum, service) => sum + (Number.parseFloat(service.amount) || 0), 0)
    const discountAmount = (subtotal * (formData.discount || 0)) / 100
    const taxAmount = ((subtotal - discountAmount) * (formData.tax || 0)) / 100
    return subtotal - discountAmount + taxAmount
  }

  const handleSave = () => {
    const totalAmount = calculateTotal()

    if (selectedBill) {
      actions.updateBill({
        ...selectedBill,
        ...formData,
        totalAmount,
        status: "Pending",
      })
    } else {
      actions.addBill({
        id: Date.now(),
        billNumber: `B${(state.bills.length + 1).toString().padStart(4, "0")}`,
        date: new Date().toISOString().split("T")[0],
        totalAmount,
        paidAmount: 0,
        status: "Pending",
        paymentMethod: "",
        ...formData,
      })
    }
    handleClose()
  }

  const handlePayment = () => {
    const updatedBill = {
      ...selectedBill,
      paidAmount: selectedBill.paidAmount + Number.parseFloat(paymentData.amount),
      paymentMethod: paymentData.method,
    }
    updatedBill.status = updatedBill.paidAmount >= updatedBill.totalAmount ? "Paid" : "Partial"

    actions.updateBill(updatedBill)
    handleClose()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "success"
      case "Partial":
        return "warning"
      case "Pending":
        return "error"
      default:
        return "default"
    }
  }

  const totalRevenue = state.bills.reduce((sum, bill) => sum + bill.paidAmount, 0)
  const pendingAmount = state.bills.reduce((sum, bill) => sum + (bill.totalAmount - bill.paidAmount), 0)

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Billing Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Create New Bill
        </Button>
      </Box>

      {/* Revenue Summary */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4" color="success.main">
                ₹{totalRevenue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Amount
              </Typography>
              <Typography variant="h4" color="error.main">
                ₹{pendingAmount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Bills
              </Typography>
              <Typography variant="h4">{state.bills.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bill Number</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Paid Amount</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.bills.map((bill) => (
              <TableRow key={bill.id}>
                <TableCell>{bill.billNumber}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{bill.patientName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {bill.patientId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{bill.date}</TableCell>
                <TableCell>₹{bill.totalAmount.toLocaleString()}</TableCell>
                <TableCell>₹{bill.paidAmount.toLocaleString()}</TableCell>
                <TableCell>₹{(bill.totalAmount - bill.paidAmount).toLocaleString()}</TableCell>
                <TableCell>
                  <Chip label={bill.status} color={getStatusColor(bill.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="View">
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton size="small">
                      <Print />
                    </IconButton>
                  </Tooltip>
                  {bill.status !== "Paid" && (
                    <Tooltip title="Record Payment">
                      <IconButton size="small" onClick={() => handlePaymentOpen(bill)}>
                        <Payment />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(bill)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Bill Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedBill ? "Edit Bill" : "Create New Bill"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient Name"
                value={formData.patientName}
                onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient ID"
                value={formData.patientId}
                onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Services
              </Typography>
              {formData.services.map((service, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Service Name"
                      value={service.name}
                      onChange={(e) => updateService(index, "name", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={service.amount}
                      onChange={(e) => updateService(index, "amount", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeService(index)}
                      disabled={formData.services.length === 1}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button variant="outlined" onClick={addService}>
                Add Service
              </Button>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount (%)"
                type="number"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax (%)"
                type="number"
                value={formData.tax}
                onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Total Amount: ₹{calculateTotal().toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedBill ? "Update" : "Create"} Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={paymentData.method}
                onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Method</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Insurance">Insurance</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handlePayment} variant="contained">
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
